import { datadogRum } from '@datadog/browser-rum';
import { messenger } from '@side/next-zendesk-sdk';
import { useCallback, useEffect, useState } from 'react';

const LAUNCHER_SELECTOR = 'iframe#launcher';

function observeLauncher(onFound: (launcher: HTMLElement) => void) {
  const el = document.querySelector(LAUNCHER_SELECTOR);

  if (el instanceof HTMLElement) {
    onFound(el);
    return () => {
      // noop: nothing to clean up when the launcher already exists
    };
  }

  const observer = new MutationObserver(() => {
    const launcherEl = document.querySelector(LAUNCHER_SELECTOR);
    if (launcherEl instanceof HTMLElement) {
      observer.disconnect();
      onFound(launcherEl);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
  };
}

/**
 * Manages Zendesk messenger launcher visibility. Hides the launcher by default
 * and only shows it when the chat window is open, giving users a way to close
 * the chat.
 */
export function useMessengerLauncherVisibility() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isReady) return;

    let launcher: HTMLElement | null = null;
    let reportedMissingLauncher = false;

    // Show launcher when chat opens (gives users a way to close it)
    messenger.onOpen(() => {
      if (launcher) {
        launcher.style.visibility = 'visible';
        return;
      }

      if (!reportedMissingLauncher) {
        reportedMissingLauncher = true;

        // The chat opened but we never found the launcher, meaning the widget
        // provably loaded and rendered but our selector no longer matches
        // Zendesk's markup — report it.
        datadogRum.addError(
          new Error(
            `Zendesk messenger opened but "${LAUNCHER_SELECTOR}" was never found`,
          ),
        );
      }
    });

    // Hide launcher when chat closes
    messenger.onClose(() => {
      if (launcher) {
        launcher.style.visibility = 'hidden';
      }
    });

    return observeLauncher((el) => {
      launcher = el;

      // Hide launcher by default
      launcher.style.visibility = 'hidden';
    });
  }, [isReady]);

  const onReady = useCallback(() => {
    setIsReady(true);
  }, []);

  return { onReady };
}
